import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Search, X } from 'lucide-react';
import { debounce } from 'lodash';
import { useMapStore } from '@/stores/mapStore';
import { usePlacesAutocomplete } from '@/hooks/usePlacesAutocomplete';
import { useHighlightLayer } from '../hooks/useHighlightLayer';
import { useSearchMarkerLayer } from '../hooks/useSearchMarkerLayer';
import { fromLonLat } from 'ol/proj';
import { useMapZoom } from '../hooks/useMapZoom';
import { normalizeText } from '@/helpers/normalizeText';
import { useViewFeature } from '../hooks/useViewFeature';
import { LayerDefinition } from '@packages/database/types';
import BaseLayer from 'ol/layer/Base';
import LayerGroup from 'ol/layer/Group';
import Map from 'ol/Map';
import { useSelectionStore } from '@/stores/selectionStore';

export interface Suggestion {
  id: string;
  display: string;
  subtitle: string;
  layerKey: string;
  category: string;
  feature?: any;
  layer?: LayerDefinition;
}

export type SearchResults = Record<string, Suggestion[]>;

// Recursively get all OL layers including those inside LayerGroups
function getAllOLLayers(map: Map): BaseLayer[] {
  const result: BaseLayer[] = [];

  map.getLayers().forEach((l) => {
    if (l instanceof LayerGroup) {
      l.getLayers().forEach((child) => result.push(child));
    } else {
      result.push(l);
    }
  });

  return result;
}

// Find the OL layer that matches a layer definition
// Uses 'config' property as that's what your VectorLayer component sets
function findOLLayer(
  map: Map,
  layerDefId: string,
): VectorLayer<VectorSource> | null {
  const match = getAllOLLayers(map).find((l) => {
    const config = l.get('config') as LayerDefinition | undefined;
    return config?.id === layerDefId;
  });

  return match instanceof VectorLayer
    ? (match as VectorLayer<VectorSource>)
    : null;
}

export const MapSearchComponent = ({ map }: { map: Map | null }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>({});
  const [isLoading, setIsLoading] = useState(false);

  const searchContainerRef = useRef<HTMLDivElement>(null);

  const setSelection = useSelectionStore((s) => s.setSelection);
  const clearSelection = useSelectionStore((s) => s.clear);

  const { layers } = useMapStore();
  const { clearHighlight } = useHighlightLayer();
  const { zoomToCoordinates } = useMapZoom();
  const { addMarker, clearMarker } = useSearchMarkerLayer(map);
  const { predictions, clearPredictions, fetchPredictions, getPlaceDetails } =
    usePlacesAutocomplete();

  

  const searchMapLayers = useCallback(
    (searchTerm: string): SearchResults => {
      if (!map) return {};

      const rawQuery = searchTerm.toUpperCase();
      const cleanQuery = normalizeText(searchTerm);
      const newResults: SearchResults = {};

      layers.forEach((layerDef) => {
        if (!layerDef.isVisible) return;

        const olLayer = findOLLayer(map, layerDef.id);
        if (!olLayer) return;

        const source = olLayer.getSource();
        if (!source) return;

        // Searchable fields from schemaConfig
        const searchableFields = layerDef.schemaConfig.properties
          .filter((p) => p.isSearchable)
          .map((p) => p.alias);

        if (searchableFields.length === 0) return;

        // Display field — showOnMap first, then showInCard, then fallback
        const displayField =
          layerDef.schemaConfig.properties.find((p) => p.isLabel)?.alias ??
          'id';
        const subtitleField =
          layerDef.schemaConfig.properties.find(
            (p) => p.showInCard && !p.isLabel,
          )?.alias ?? null;

        const matches: Suggestion[] = [];

        source.getFeatures().forEach((feature) => {
          const props = feature.getProperties();

          const isMatch = searchableFields.some((field) => {
            const val = props[field];
            if (val == null) return false;
            const str = String(val);
            return (
              str.toUpperCase().includes(rawQuery) ||
              normalizeText(str).includes(cleanQuery)
            );
          });

          if (!isMatch) return;

          matches.push({
            id: feature.getId()?.toString() ?? props.id ?? '',
            display: String(
              props[displayField] ?? props.id ?? feature.getId() ?? '',
            ),
            subtitle: subtitleField
              ? String(props[subtitleField] ?? layerDef.displayName)
              : layerDef.displayName,
            layerKey: layerDef.key,
            category: layerDef.displayName,
            feature,
            layer: layerDef,
          });
        });

        if (matches.length > 0) {
          newResults[layerDef.key] = matches.slice(0, 5);
        }
      });

      return newResults;
    },
    [map, layers],
  );

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (!value || value.length < 2) return;
      const mapResults = searchMapLayers(value);
      fetchPredictions(value);
      setResults(mapResults);
      setIsLoading(false);
    }, 300),
    [searchMapLayers, fetchPredictions],
  );

  // Merge Google Places predictions into results
  useEffect(() => {
    if (!predictions?.length) return;
    setResults((prev) => ({
      ...prev,
      PLACES: predictions.slice(0, 3).map((p) => ({
        id: p.place_id,
        display: p.description,
        subtitle: 'Google Places',
        layerKey: 'PLACES',
        category: 'Places',
      })),
    }));
  }, [predictions]);

  // Click outside closes results
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!searchContainerRef.current?.contains(e.target as Node)) {
        setResults({});
        clearHighlight();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [clearHighlight]);

  // =========================================================================
  // SELECTION
  // =========================================================================

  const handleSelect = async (item: Suggestion) => {
    setQuery(item.display);
    setResults({});
    clearMarker();
    clearHighlight();

    // Google Place
    if (item.layerKey === 'PLACES' || !item.feature) {
      getPlaceDetails(item.id, (place: any, status: any) => {
        if (status === 'OK' && place?.geometry?.location && map) {
          const lat = place.geometry.location.lat();
          const lon = place.geometry.location.lng();
          zoomToCoordinates(lon, lat);
          addMarker(
            fromLonLat([lon, lat], map.getView().getProjection().getCode()),
          );
        }
      });
      return;
    }

    setSelection(
      [
        {
          id: item.id,
          feature: item.feature,
          layerKey: item.layerKey,
          data: item.feature.getProperties(),
          layer: item.layer,
        },
      ],
      'search',
    );
  };

  // =========================================================================
  // INPUT HANDLERS
  // =========================================================================

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);

    if (val.length < 2) {
      setResults({});
      clearMarker();
      clearHighlight();
      clearPredictions();
      return;
    }

    setIsLoading(true);
    debouncedSearch(val);
  };

  const handleClear = () => {
    setQuery('');
    setResults({});
    clearMarker();
    clearHighlight();
    clearPredictions();
  };

  const hasResults = Object.values(results).some((arr) => arr.length > 0);

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div
      ref={searchContainerRef}
      className="absolute top-4 left-4 right-4 md:right-auto md:w-96 z-30 pointer-events-auto"
    >
      <div className="bg-card rounded-xl shadow-xl border border-border overflow-hidden">
        {/* Input */}
        <div className="flex items-center px-4 py-3 bg-card">
          <Search
            className={`w-5 h-5 mr-3 ${isLoading ? 'text-secondary animate-pulse' : 'text-slate-400'}`}
          />
          <input
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-slate-400"
            placeholder="Search assets, tickets, places..."
            value={query}
            onChange={handleInput}
          />
          {query && (
            <button onClick={handleClear}>
              <X className="cursor-pointer w-4 h-4 text-foreground hover:text-secondary" />
            </button>
          )}
        </div>

        {/* Loading skeleton */}
        {isLoading && !hasResults && query.length > 2 && (
          <div className="px-4 py-3 space-y-3">
            <div className="h-2 w-16 bg-slate-100 rounded animate-pulse" />
            <div className="space-y-2">
              <div className="h-8 w-full bg-slate-50 rounded animate-pulse" />
              <div className="h-8 w-full bg-slate-50 rounded animate-pulse" />
            </div>
          </div>
        )}

        {/* Results */}
        {hasResults && (
          <div className="max-h-[60vh] overflow-y-auto bg-background">
            {Object.entries(results).map(([layerKey, items]) => (
              <SuggestionGroup
                key={layerKey}
                title={items[0]?.category ?? layerKey}
                items={items}
                onSelect={handleSelect}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// =========================================================================
// SUGGESTION GROUP
// =========================================================================

interface SuggestionGroupProps {
  title: string;
  items: Suggestion[];
  onSelect: (item: Suggestion) => void;
}

const SuggestionGroup = ({ title, items, onSelect }: SuggestionGroupProps) => {
  if (!items.length) return null;

  return (
    <div className="py-2">
      <div className="px-3 py-1 text-[10px] font-bold text-foreground uppercase tracking-wider">
        {title}
      </div>
      {items.map((item) => (
        <button
          key={item.id}
          onClick={() => onSelect(item)}
          className="w-full text-left flex items-center gap-3 px-3 py-1.5 hover:bg-muted transition-colors group cursor-pointer"
        >
          {/* Color dot from layer styles */}
          {item.layer && (
            <div
              className="w-2 h-2 rounded-full shrink-0 border border-border"
              style={{ backgroundColor: item.layer.styles?.color ?? '#94a3b8' }}
            />
          )}

          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-foreground truncate group-hover:text-slate-900">
              {item.display}
            </div>
            {item.subtitle && (
              <div className="text-xs text-muted-foreground truncate">
                {item.subtitle}
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
};
